import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { doc, getDoc, setDoc, collection } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import { canManageStaff, getPermissionErrorMessage, getAvailableRoles, canAssignRole, getRoleAssignmentErrorMessage } from '../services/staffPermissionService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import defaultProfilePic from '../assets/default-profile.jpg';

interface Business {
  id: string;
  name: string;
}

interface LocationState {
  userId: string;
  userData: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    profilePictureURL?: string;
  };
  email: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
  
  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%; /* Full width as requested */
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const UserInfoContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ProfileImage = styled.div`
  width: 80px; /* Reduced from 100px */
  height: 80px; /* Reduced from 100px */
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UserName = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const UserEmail = styled.div`
  font-size: 16px;
  color: #666;
`;

const UserPhone = styled.div`
  font-size: 16px;
  color: #666;
`;

const RoleContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const RoleLabel = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
`;

const RoleSelect = styled.select`
  width: 100%;
  padding: 14px;
  font-size: 16px;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  font-family: 'Montserrat', sans-serif;
  outline: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  color: #333;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 16px;
`;

const ButtonContainer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
`;

const ErrorMessage = styled.div`
  color: #e53935;
  padding: 16px;
  background-color: #ffebee;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  color: #43a047;
  padding: 16px;
  background-color: #e8f5e9;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const PermissionErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 20px;
  background-color: #f9f9f9;
  border-radius: 12px;
  margin-top: 40px;
`;

const PermissionErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const PermissionErrorMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  max-width: 400px;
  line-height: 1.5;
`;

const AddExistingStaffPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessId } = useParams<{ businessId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState('staff');  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [permissionError, setPermissionError] = useState<string>('');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  
  const locationState = location.state as LocationState | undefined;
  
  useEffect(() => {
    const fetchData = async () => {
      if (!businessId || !locationState) {
        navigate(`/business/${businessId}/staff/lookup`);
        return;
      }

      try {
        setLoading(true);
        const currentUser = await getCurrentUser();
        setUser(currentUser);

        if (!currentUser?.id) {
          navigate('/login');
          return;
        }        // Check if user has permission to manage staff
        const hasPermission = await canManageStaff(currentUser.id, businessId);
        if (!hasPermission) {
          setPermissionError(getPermissionErrorMessage('add-staff'));
          setLoading(false);
          return;
        }

        // Get available roles for this user
        const roles = await getAvailableRoles(currentUser.id, businessId);
        setAvailableRoles(roles);
        
        // Set default role to the lowest available role
        if (roles.length > 0) {
          setRole(roles[roles.length - 1]); // Last item is the lowest role
        }

        // Fetch business details
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        
        if (businessDoc.exists()) {
          setBusiness({
            id: businessDoc.id,
            name: businessDoc.data().name
          });
        } else {
          console.error("Business not found");
          navigate('/businesses');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId, navigate, locationState]);

  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleNavigateToMyBusinesses = () => {
    navigate('/businesses');
  };
  
  const handleNavigateToSettings = () => {
    navigate(`/business/${businessId}/settings`);
  };
  
  const handleNavigateToStaff = () => {
    navigate(`/business/${businessId}/staff`);
  };
    const handleAddStaff = async () => {
    if (!businessId || !locationState) return;
    
    setError('');
    setSuccess('');

    // Check if user can assign the selected role
    const currentUser = await getCurrentUser();
    if (currentUser?.id) {
      const canAssign = await canAssignRole(currentUser.id, businessId, role as any);
      if (!canAssign) {
        const userRole = await import('../services/staffPermissionService').then(service => 
          service.getUserRole(currentUser.id, businessId)
        );
        setError(getRoleAssignmentErrorMessage(userRole, role as any));
        return;
      }
    }
    
    setSaving(true);
    
    try {
      const newStaffRef = doc(collection(db, 'staff'));

      const phoneNumber = userData.phoneNumber || '';
      
      await setDoc(newStaffRef, {
        userId: locationState.userId,
        firstName: locationState.userData.firstName || '',
        lastName: locationState.userData.lastName || '',
        email: locationState.userData.email || '',
        phone: phoneNumber,        phoneNumber: phoneNumber,
        photoUrl: locationState.userData.profilePictureURL || '',
        role,
        businessId,
        createdAt: new Date()
      });

      const userDocRef = doc(db, 'users', locationState.userId);
      await setDoc(userDocRef, {
        role: 'manager',
      }, { merge: true });
      
      setSuccess('Staff member added successfully');
      
      setTimeout(() => {
        navigate(`/business/${businessId}/staff`);
      }, 2000);
    } catch (error) {
      console.error('Error adding staff member:', error);
      setError('Failed to add staff member. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          <LoadingContainer>Loading...</LoadingContainer>
        </ContentWrapper>
      </Container>
    );
  }

  // Handle permission error
  if (permissionError) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Access Denied</title>
        </Helmet>
        <Container>
          <ContentWrapper>
            <TopBar user={user} />
            <PermissionErrorContainer>
              <PermissionErrorTitle>Access Denied</PermissionErrorTitle>
              <PermissionErrorMessage>{permissionError}</PermissionErrorMessage>
              <Button 
                text="Back to Team" 
                onClick={() => navigate(`/business/${businessId}/staff`)}
                bgColor="#000"
                textColor="#fff"
                width="auto"
                minWidth="180px"
              />
            </PermissionErrorContainer>
          </ContentWrapper>
        </Container>
      </>
    );
  }
  
  if (!locationState) {
    navigate(`/business/${businessId}/staff/lookup`);
    return null;
  }
  
  const { userData } = locationState;
  
  const phoneForDisplay = userData.phoneNumber || 'No phone number';
  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Add Existing Staff</title>
        <meta name="description" content={`${APP_NAME} - Add Existing Staff`} />
      </Helmet>
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          
          <PageTitle>Add Team Member</PageTitle>
        
        <Breadcrumbs>
          <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>Businesses</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToSettings}>
            <BreadcrumbText>{business?.name}</BreadcrumbText>
          </BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToStaff}>Team</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <span>Add</span>
        </Breadcrumbs>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <FormContainer>
          <UserInfoContainer>            <ProfileImage>
              <img 
                src={userData.profilePictureURL || defaultProfilePic} 
                alt={`${userData.firstName} ${userData.lastName}`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = defaultProfilePic;
                }}
              />
            </ProfileImage>
            
            <UserDetails>
              <UserName>{userData.firstName} {userData.lastName}</UserName>
              <UserEmail>{userData.email}</UserEmail>
              <UserPhone>{phoneForDisplay}</UserPhone>
            </UserDetails>
          </UserInfoContainer>
            <RoleContainer>
            <RoleLabel>Role</RoleLabel>
            <RoleSelect
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {availableRoles.map((roleOption) => (
                <option key={roleOption} value={roleOption}>
                  {roleOption.charAt(0).toUpperCase() + roleOption.slice(1)}
                </option>
              ))}
            </RoleSelect>
          </RoleContainer>
          
          <ButtonContainer>
            <Button
              text="Add"
              onClick={handleAddStaff}
              loading={saving}
              bgColor="#000"
              textColor="#fff"
              width="250px"
              minWidth="250px"
            />
          </ButtonContainer>        </FormContainer>
      </ContentWrapper>
    </Container>
    </>
  );
};

export default AddExistingStaffPage;

// Last modified: 2025-04-11 17:24:05 by rinae-del